import type {
  BossDifficulty,
  BossInputEvent,
  BossRuntimeConfig,
  BossSnapshot,
} from '@shared/boss';
import { useAuthStore } from '@/auth/authStore';
import { COMMON_HEADERS, SERVER_URL } from '@/config/serverUrl';

export interface BossListItem {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  imageUrl: string | null;
  accentColor: string;
  durationMs: number;
  rewards: {
    normalCoins: number;
    hardCoins: number;
    firstClearGems: number;
    dailyLimit: number;
    frameId: string | null;
    frame: {
      id: string;
      name: string;
      iconUrl: string | null;
      owned: boolean;
      requiredPerfectRate: number;
    } | null;
  };
  progress: {
    normalVictories: number;
    hardVictories: number;
    bestNormalTimeMs: number | null;
    bestHardTimeMs: number | null;
  };
  hardUnlocked: boolean;
}

export interface BossRunTicket {
  mode?: 'solo' | 'clan';
  resumed?: boolean;
  runId: string;
  seed: number;
  startedAt: string;
  config: BossRuntimeConfig;
  clanBoss?: {
    level: number;
    hp: number;
    maxHp: number;
    rewardGems: number;
  };
}

export interface BossRotation {
  currentDay: number;
  nextChangeAt: string;
  weekStart: string;
  weekEnd: string;
  clearedDays: number[];
  clearedCount: number;
  schedule: Array<{
    day: number;
    bossId: string;
    slug: string;
    name: string;
    imageUrl: string | null;
    accentColor: string;
    isToday: boolean;
    cleared: boolean;
  }>;
  milestones: Array<{
    required: number;
    coins: number;
    gems: number;
    frame: boolean;
    claimed: boolean;
  }>;
}

export interface BossCompetitionEntry {
  rank: number;
  userId: string;
  displayName: string;
  rankTier: string;
  score: number;
}

export interface BossCompetitionOverview {
  enabled: boolean;
  dailyRunLimit: number;
  attemptsUsed: number;
  attemptsRemaining: number;
  rankingOpen: boolean;
  scoreDate: string;
  dayEndsAt: string;
  weekStart: string;
  weekEndsAt: string;
  myDailyScore?: number;
  /** Compatibility alias returned for older app versions. */
  myDailyDamage: number;
  myDailyRank: number | null;
  myWeeklyPoints: number;
  myWeeklyRank: number | null;
  dailyTop: BossCompetitionEntry[];
  weeklyTop: BossCompetitionEntry[];
  dailyRankPoints: Array<{ from: number; to: number; points: number }>;
  weeklyRewards: Array<{
    from: number;
    to: number;
    coins: number;
    gems: number;
    frameId: string | null;
  }>;
}

export interface BossRunSettlement {
  outcome: 'win' | 'lose';
  snapshot: BossSnapshot;
  rewards: {
    coins: number;
    gems: number;
    frameGranted: boolean;
    frameRequirement?: {
      requiredPerfectRate: number;
      actualPerfectRate: number;
      met: boolean;
    } | null;
    dailyCapReached: boolean;
    dailyCoins?: number;
    firstClearGems?: number;
    weeklyBonus?: {
      coins: number;
      gems: number;
      frameGranted: boolean;
      clearedCount: number;
    };
  };
  wallet: { coins: number; gems: number };
  clanBoss?: {
    damage: number;
    attackedLevel: number;
    levelDefeated: boolean;
    completedSeason?: boolean;
    eligibleForReward?: boolean;
    minPerfectHits?: number;
    contributorsRewarded: number;
    rewardGemsEach: number;
    level: number;
    hp: number;
    maxHp: number;
    title?: string | null;
  };
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
): Promise<T> {
  const token = useAuthStore.getState().token;
  const response = await fetch(`${SERVER_URL}${path}`, {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...COMMON_HEADERS,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: body == null ? undefined : JSON.stringify(body),
    credentials: 'include',
  });
  if (!response.ok) {
    let message = `Boss API ${response.status}`;
    try {
      const payload = (await response.json()) as {
        message?: string;
        error?: string;
      };
      message = payload.message ?? payload.error ?? message;
    } catch {
      // Non-JSON infrastructure response.
    }
    throw new Error(message);
  }
  return response.json() as Promise<T>;
}

export const bossApi = {
  list: () =>
    request<{
      enabled: boolean;
      bosses: BossListItem[];
      rotation?: BossRotation | null;
      competition?: BossCompetitionOverview | null;
    }>('GET', '/bosses/'),
  competition: () =>
    request<BossCompetitionOverview>('GET', '/bosses/competition'),
  begin: (bossId: string, difficulty: BossDifficulty) =>
    request<BossRunTicket>('POST', '/bosses/runs', { bossId, difficulty }),
  finish: (
    runId: string,
    elapsedMs: number,
    events: readonly BossInputEvent[],
  ) =>
    request<BossRunSettlement>('POST', `/bosses/runs/${runId}/finish`, {
      elapsedMs,
      events,
    }),
};
